<template>
  <RevInfoBlock
    v-if="shouldDisplayTimeError"
    :icon="IconWarning"
    :title="i18n(parentTranslations.errorPaidMethodTimeNotFastEnough)"
    variant="danger"
  />

  <div v-if="!shouldDisplayTimeError">
    <div
      v-if="
        FF_ENABLE_NEW_SHIPPING_BACKSHIP_CARRIERS && numberOfBackshipOptions > 0
      "
      class="text-static-default-hi body-2-bold mb-8"
    >
      {{
        i18n(translations.backshipOptions, {
          count: numberOfBackshipOptions,
        })
      }}
    </div>
    <div class="grid-cols-3 gap-16 space-y-16 md:grid md:space-y-0">
      <RevInputSelect
        :id="`${FORM_KEY}-shipping-provider`"
        v-model="formValues.shippingProvider"
        :label="i18n(categoryListTranslations.shippingProvider)"
        :options="providerOptions"
        @update:model-value="
          () =>
            FF_ENABLE_NEW_SHIPPING_BACKSHIP_CARRIERS
              ? handleSelectShippingProvider()
              : ''
        "
      />
      <RevInputSelect
        :id="`${FORM_KEY}-shipping-time`"
        v-model="formValues.shippingTime"
        :disabled="
          FF_ENABLE_NEW_SHIPPING_BACKSHIP_CARRIERS
            ? isShippingTimeDisabled
            : false
        "
        :label="
          FF_ENABLE_NEW_SHIPPING_BACKSHIP_CARRIERS
            ? shippingTimeLabel
            : i18n(categoryListTranslations.shippingTime)
        "
        :options="shippingTimeOptions"
      />
      <div class="flex space-x-16">
        <RevInputVirtual
          v-if="isFree"
          class="w-full"
          disabled
          :filled="false"
          :focused="false"
          :has-clear-button="false"
          :helper-text-id="i18n(categoryListTranslations.free)"
          :invalid="false"
          :label="i18n(categoryListTranslations.free)"
          :touched="false"
        />
        <RevInputNumber
          v-if="!isFree"
          :id="`${FORM_KEY}-shipping-price`"
          v-model="formValues.price"
          class="w-full"
          :icon="getCurrencyIcon(currency)"
          :label="i18n(categoryListTranslations.price)"
          :max="maxPrice"
          :min="0"
          :show-clear-button="false"
          :show-stepper="false"
        />
        <RevButton
          :disabled="!isValidated"
          variant="primary"
          @click="handleAddMethod"
        >
          {{ i18n(translations.add) }}
        </RevButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { computed, ref, watch } from 'vue'

import { Currency } from '@backmarket/http-api'
import type { MarketWithSubMarkets } from '@backmarket/http-api/src/api-specs-shipping/rules'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { setFormValues } from '@ds/components/Form/Form.actions'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputNumber } from '@ds/components/InputNumber'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputVirtual } from '@ds/components/InputVirtual'
import { IconCurrencyAustralianDollar } from '@ds/icons/IconCurrencyAustralianDollar'
import { IconCurrencyDollar } from '@ds/icons/IconCurrencyDollar'
import { IconCurrencyEuro } from '@ds/icons/IconCurrencyEuro'
import { IconCurrencyPound } from '@ds/icons/IconCurrencyPound'
import { IconCurrencySwedishKrone } from '@ds/icons/IconCurrencySwedishKrone'
import { IconCurrencyYen } from '@ds/icons/IconCurrencyYen'
import { IconWarning } from '@ds/icons/IconWarning'

import { useFormatHours } from '~/composables/useFormatHours'

import { FORM_IDS } from '../../../../../../../../Shipping.constants'
import { NO_CURRENCY } from '../../../../../../CategoryList.constants'
import categoryListTranslations from '../../../../../../CategoryList.translations'
import type {
  PotentialCurrency,
  ShippingCategoryGroupMetadata,
} from '../../../../../../CategoryList.types'
import type {
  FormMethodValues,
  ShippingValidationThresholds,
} from '../../../../RuleDrawer.types'
import {
  formatMethod,
  getCurrentMethodsKey,
  getMinShippingTime,
  isMethodValid,
  isPaidMethodTimeFastEnough,
} from '../../../../RuleDrawer.utils'
import parentTranslations from '../../RuleFormFields.translations'

import translations from './AddMethod.translations'

const { FF_ENABLE_NEW_SHIPPING_BACKSHIP_CARRIERS } = useRuntimeConfig().public

const FORM_KEY = crypto.randomUUID()
const hasNewThresholdFormat =
  useRuntimeConfig().public.FF_ENABLE_NEW_SHIPPING_THRESHOLD_FORMAT

const props = defineProps<{
  currentMethods: FormMethodValues[]
  otherMethods: FormMethodValues[]
  markets: MarketWithSubMarkets[]
  currency: PotentialCurrency
  isFree: boolean
  isFreeMandatory: boolean
  categoryGroupMetadata: ShippingCategoryGroupMetadata
  validationThresholds: ShippingValidationThresholds | null
  shouldShowCollectionPointOptions: boolean
}>()

const emit = defineEmits(['method-add'])

const i18n = useI18n()
const { getFormatHoursInDay } = useFormatHours()

const initialValues: FormMethodValues = {
  key: '',
  markets: [],
  shippingProvider: '',
  shippingTime: '',
  price: 0,
  currency: props.currency,
  isCollectionPoint: props.shouldShowCollectionPointOptions,
}

const formValues = ref<FormMethodValues>(initialValues)

const currentMinimumFreeShippingTime = computed(() =>
  getMinShippingTime(
    props.otherMethods,
    props.markets
      .map(({ market, subMarkets }) =>
        subMarkets.length > 0
          ? subMarkets.map(({ subMarketCode }) => subMarketCode)
          : market,
      )
      .flat(),
  ),
)

const providerOptions = computed(() => {
  // Providers need to be available in every selected market
  return props.categoryGroupMetadata.shippingMethods.providerOptions.filter(
    ({ carrier }) => {
      const isNotRestricted =
        !carrier.restrictedToMarkets ||
        props.markets.every((marketWithSubMarkets) =>
          carrier.restrictedToMarkets?.includes(marketWithSubMarkets.market),
        )

      if (
        FF_ENABLE_NEW_SHIPPING_BACKSHIP_CARRIERS &&
        props.shouldShowCollectionPointOptions
      ) {
        return carrier.collectionPoint
      }

      if (FF_ENABLE_NEW_SHIPPING_BACKSHIP_CARRIERS && carrier.backship) {
        const shouldIncludeCollectionPoint =
          carrier.collectionPoint && !props.shouldShowCollectionPointOptions

        if (shouldIncludeCollectionPoint) {
          return false
        }

        const currentMarkets = props.markets.map(({ market }) => market)
        // A backship carrier is available in a country when:
        // 1. The country exists as a key in backshipTransitTimes
        // 2. The transit time is valid:
        //    - Always valid for free shipping
        //    - Must be faster than minimum free shipping time for paid shipping
        const carrierIsAvailableInEverySelectedMarket = currentMarkets.every(
          (market) => {
            const isAvailable = market in carrier.backshipTransitTimes

            if (!isAvailable) return isAvailable

            const isTransitTimeValid =
              props.isFree || !currentMinimumFreeShippingTime.value
                ? true
                : carrier.backshipTransitTimes[market] <
                  currentMinimumFreeShippingTime.value

            return isTransitTimeValid
          },
        )

        return isNotRestricted && carrierIsAvailableInEverySelectedMarket
      }

      return isNotRestricted
    },
  )
})

const numberOfBackshipOptions = computed(() => {
  return providerOptions.value.filter(({ carrier }) => carrier.backship).length
})

const shippingTimeOptions = computed(() => {
  if (!props.validationThresholds) {
    return []
  }
  const allShippingTimeOptions =
    props.validationThresholds.shippingTimes.values.map((value) => ({
      value: value.toString(),
      label: getFormatHoursInDay(value),
    }))

  if (props.isFree) {
    return allShippingTimeOptions
  }

  if (!currentMinimumFreeShippingTime.value) {
    return allShippingTimeOptions
  }

  return allShippingTimeOptions.filter((method) =>
    isPaidMethodTimeFastEnough(
      +method.value,
      currentMinimumFreeShippingTime.value,
    ),
  )
})

const shouldDisplayTimeError = computed(() => {
  if (!props.isFreeMandatory) return false

  return (
    !props.isFree &&
    props.validationThresholds &&
    !shippingTimeOptions.value.length
  )
})

const maxPrice = computed(() => {
  if (hasNewThresholdFormat) {
    const maxCurrencyPrice =
      props.validationThresholds?.pricesByCurrency[props.currency]?.max

    return typeof maxCurrencyPrice === 'number' && maxCurrencyPrice > 0
      ? maxCurrencyPrice
      : undefined
  }

  return typeof props.validationThresholds?.prices.max === 'number' &&
    props.validationThresholds?.prices.max > 0
    ? props.validationThresholds.prices.max
    : undefined
})

const isValidated = computed(() => {
  if (!props.validationThresholds) {
    return false
  }
  const isValid = isMethodValid(
    { ...formValues.value, markets: props.markets },
    props.currentMethods,
    props.otherMethods,
    props.isFree,
    props.isFreeMandatory,
  )

  return (
    isValid &&
    (props.isFree ||
      !props.isFreeMandatory ||
      +formValues.value.price <=
        (hasNewThresholdFormat
          ? props.validationThresholds.pricesByCurrency[props.currency]?.max
          : props.validationThresholds.prices.max))
  )
})

const selectedProvider = computed(() => {
  return props.categoryGroupMetadata.shippingMethods.providerOptions.find(
    ({ carrier }) => carrier.codename === formValues.value.shippingProvider,
  )
})

const isBackshipSelected = computed(() => {
  return selectedProvider.value?.carrier.backship ?? false
})

const isShippingTimeDisabled = computed(() => isBackshipSelected.value)

const shippingTimeLabel = computed(() => {
  if (!isBackshipSelected.value || !selectedProvider.value)
    return i18n(categoryListTranslations.shippingTime)

  const currentMarkets = props.markets.map(({ market }) => market)
  const transitTimes = currentMarkets.map(
    (market) => selectedProvider.value?.carrier.backshipTransitTimes[market],
  )
  const allSameTime = transitTimes.every((time) => time === transitTimes[0])

  if (allSameTime) {
    return i18n(categoryListTranslations.shippingTime)
  }

  return `${i18n(categoryListTranslations.shippingTime)} (mixed)`
})

function setBackshipShippingTime() {
  formValues.value.shippingTime = ''

  if (!selectedProvider.value) return

  const currentMarkets = props.markets.map(({ market }) => market)
  const transitTimes = currentMarkets.map(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (market) => selectedProvider.value!.carrier.backshipTransitTimes[market],
  )
  const allSameTime = transitTimes.every((time) => time === transitTimes[0])

  if (allSameTime) {
    formValues.value.shippingTime = transitTimes[0].toString()
  } else {
    formValues.value.shippingTime = 'mixed'
  }
}

function getCurrencyIcon(currency: PotentialCurrency) {
  return {
    [NO_CURRENCY]: null,
    [Currency.AUD]: IconCurrencyAustralianDollar,
    [Currency.EUR]: IconCurrencyEuro,
    [Currency.GBP]: IconCurrencyPound,
    [Currency.JPY]: IconCurrencyYen,
    [Currency.SEK]: IconCurrencySwedishKrone,
    [Currency.USD]: IconCurrencyDollar,
  }[currency]
}

function formatMixedMethods() {
  const marketsGroupByTransitTime: Record<string, MarketWithSubMarkets[]> = {}
  const newMethods: FormMethodValues[] = []

  props.markets.forEach((market) => {
    const transitTime =
      selectedProvider.value?.carrier.backshipTransitTimes[market.market]

    if (transitTime) {
      if (!marketsGroupByTransitTime[transitTime]) {
        marketsGroupByTransitTime[transitTime] = []
      }

      marketsGroupByTransitTime[transitTime].push(market)
    }
  })

  Object.keys(marketsGroupByTransitTime).forEach((transitTime) => {
    newMethods.push(
      formatMethod({
        ...formValues.value,
        markets: marketsGroupByTransitTime[transitTime],
        shippingTime: transitTime.toString(),
      }),
    )
  })

  return newMethods
}

function handleAddMethod() {
  if (isValidated.value) {
    setFormValues(FORM_IDS.SHIPPING_RULES, {
      [getCurrentMethodsKey(props.isFree)]: [
        ...props.currentMethods,
        ...(FF_ENABLE_NEW_SHIPPING_BACKSHIP_CARRIERS &&
        formValues.value.shippingTime === 'mixed'
          ? formatMixedMethods()
          : [formatMethod({ ...formValues.value, markets: props.markets })]),
      ],
    })

    formValues.value = initialValues
    emit('method-add')
  }
}

function handleSelectShippingProvider() {
  if (isBackshipSelected.value) {
    setBackshipShippingTime()
  }
}

if (FF_ENABLE_NEW_SHIPPING_BACKSHIP_CARRIERS) {
  watch(
    () => props.markets,
    () => {
      if (isBackshipSelected.value) {
        setBackshipShippingTime()
      }
    },
    { deep: true },
  )
}
</script>
